import { useState, useRef, useCallback, useEffect } from 'react';
import type {
  AnalysisType,
  ComplianceAnalysisResult,
  SocialPostMeta,
  EventActivationMeta,
  LabelUsage,
  ProductCategory,
  ServerAnalysisRecord,
} from '../../shared/types';
import { ANALYSIS_OPTIONS } from '../../shared/types';
import { analyzeContent, listServerAnalyses, getServerAnalysis, updateServerAnalysis, deleteServerAnalysis } from '../services/api';
import { resizeImageForStorage } from '../utils/image';
import { playChime } from '../utils/sound';
import { useAuth } from '../contexts/AuthContext';

// Au plus 3 analyses en parallèle : le pipeline 3 juges est lourd et soumis à
// du rate-limiting Mistral. Le reste patiente en file (status 'queued').
const MAX_CONCURRENT_JOBS = 3;
// Borne la taille du cache localStorage (résultats + images base64).
const MAX_CACHED_ITEMS = 60;

export type JobStatus = 'queued' | 'running' | 'done' | 'error';

/** Charge utile d'une analyse — identique aux options de `analyzeContent`. */
export interface AnalyzePayload {
  textContent?: string;
  context?: string;
  images?: File[];
  social?: SocialPostMeta;
  /** Qualification réglementaire du produit (étape zéro). Défaut : 'undecided'. */
  productCategory?: ProductCategory;
  labelUsage?: LabelUsage;
  event?: EventActivationMeta;
}

/** Élément unifié de la sidebar : couvre une analyse en cours, terminée,
 *  en erreur, persistée serveur, ou locale (brief créatif non persisté). */
export interface SidebarItem {
  id: string;            // stable : jobId (crypto.randomUUID) ou id serveur
  serverId?: string;     // UUID visaly_analyses si persisté côté serveur
  title: string;
  analysisType: AnalysisType;
  analysisLabel: string;
  score: number;         // -1 = sentinelle (en cours / brief créatif)
  verdict?: ComplianceAnalysisResult['verdict'];
  timestamp: string;     // ISO
  status: JobStatus;
  progress?: number;
  progressLabel?: string;
  result?: ComplianceAnalysisResult;
  errorMessage?: string;
  /** false = analyse terminée non encore consultée (pastille bleue). En mémoire. */
  read: boolean;
}

export interface AnalyzeJobInput {
  analysisType: AnalysisType;
  label: string;
  inputSummary: string;
  payload: AnalyzePayload;
}

export type NotificationKind = 'analysis_done' | 'analysis_error';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  itemId: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

function rid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `id-${Date.now()}-${Math.floor(performance.now())}`;
  }
}

function labelFor(type: AnalysisType): string {
  return ANALYSIS_OPTIONS.find((o) => o.key === type)?.label ?? type;
}

function cacheKey(userId: string | undefined): string {
  return userId ? `visaly_history_${userId}` : 'visaly_history';
}

function recordToItem(r: ServerAnalysisRecord): SidebarItem {
  // B1 : le serveur porte désormais le cycle de vie (running/done/error).
  const status: JobStatus = r.status === 'running' ? 'running' : r.status === 'error' ? 'error' : 'done';
  return {
    id: r.id,
    serverId: r.id,
    title: r.title || r.caption || labelFor(r.analysisType),
    analysisType: r.analysisType,
    analysisLabel: labelFor(r.analysisType),
    score: r.score ?? -1,
    verdict: r.verdict ?? undefined,
    timestamp: r.createdAt,
    status,
    progress: r.progress ?? undefined,
    progressLabel: r.progressLabel ?? undefined,
    errorMessage: r.errorMessage ?? undefined,
    result: r.result ?? undefined,
    read: true, // chargé du serveur = déjà vu (la complétion via poll remettra la pastille)
  };
}

function loadCache(userId: string | undefined): SidebarItem[] {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SidebarItem[];
    // On ne garde que des items terminés et on neutralise tout statut transitoire.
    return parsed
      .filter((i) => i && i.id && i.result)
      .map((i) => ({ ...i, status: 'done' as JobStatus, read: true }));
  } catch {
    return [];
  }
}

/**
 * Source unique pour : la sidebar (persistée serveur, cross-device), la file
 * d'attente d'analyses concurrentes, et le centre de notifications.
 *
 * Remplace l'ancien `useAnalysisHistory` (localStorage-only). Le serveur
 * (visaly_analyses) est la source de vérité ; le cache localStorage sert au
 * premier rendu instantané et conserve les briefs créatifs (non persistés).
 */
export function useAnalysisJobs() {
  const { user, features, organization, previewBundle, consumePreviewCredit } = useAuth();
  const userId = user?.id;
  const hasHistoryAccess = features.history || (organization?.creditBalance ?? 0) > 0;

  const [items, setItems] = useState<SidebarItem[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<AppNotification[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Ref synchronisée avec `items` pour que le scheduler lise un état à jour
  // (les setState React sont asynchrones).
  const itemsRef = useRef<SidebarItem[]>([]);
  const jobsRef = useRef<Map<string, AnalyzeJobInput>>(new Map());
  // Reflète previewBundle au moment où un job se termine (évite une closure périmée).
  const previewBundleRef = useRef(previewBundle);
  useEffect(() => { previewBundleRef.current = previewBundle; }, [previewBundle]);

  // B1 — pollers actifs pour les jobs 'running' réattachés (après refresh /
  // réouverture). pollRef casse la dépendance circulaire avec l'hydratation.
  const pollersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const pollRef = useRef<(serverId: string) => void>(() => {});

  const setItemsSynced = useCallback(
    (updater: (prev: SidebarItem[]) => SidebarItem[]) => {
      itemsRef.current = updater(itemsRef.current);
      setItems(itemsRef.current);
    },
    [],
  );

  const persistCache = useCallback(() => {
    if (!userId || !hasHistoryAccess) return;
    try {
      const toStore = itemsRef.current
        .filter((i) => i.status === 'done' && i.result)
        .slice(0, MAX_CACHED_ITEMS);
      localStorage.setItem(cacheKey(userId), JSON.stringify(toStore));
    } catch {
      /* quota dépassé — on ignore, le serveur reste la source de vérité */
    }
  }, [userId, hasHistoryAccess]);

  const updateItem = useCallback(
    (id: string, patch: Partial<SidebarItem>) => {
      setItemsSynced((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
      persistCache();
    },
    [setItemsSynced, persistCache],
  );

  const pushNotification = useCallback(
    (kind: NotificationKind, itemId: string, title: string) => {
      // Tronque les titres longs (requêtes verbeuses) pour des notifs lisibles.
      const short = title.length > 40 ? title.slice(0, 40).trimEnd() + '…' : title;
      const message =
        kind === 'analysis_done'
          ? `Votre analyse pour « ${short} » est prête — cliquez pour la consulter.`
          : `L'analyse pour « ${short} » a échoué — aucun crédit n'a été débité. Réessayez.`;
      const notif: AppNotification = { id: rid(), kind, itemId, title: short, message, read: false, createdAt: new Date().toISOString() };
      setNotifications((prev) => [notif, ...prev].slice(0, 40));
      // Toast transitoire (slide-in bas-droite) + petit son sur succès.
      setToasts((prev) => [notif, ...prev].slice(0, 4));
      if (kind === 'analysis_done') playChime();
    },
    [],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Runner + scheduler ────────────────────────────────
  // `scheduleRef` casse la dépendance circulaire runJob ↔ schedule.
  const scheduleRef = useRef<() => void>(() => {});

  const runJob = useCallback(
    async (id: string) => {
      const job = jobsRef.current.get(id);
      if (!job) return;
      updateItem(id, { status: 'running', progress: 0, progressLabel: 'Démarrage…' });
      try {
        const data = job.payload;
        const resp = await analyzeContent(job.analysisType, data, (ev) => {
          updateItem(id, { progress: ev.progress, progressLabel: ev.label });
        });
        if (resp.success && resp.result) {
          const result = resp.result;
          result.originalInput = data.textContent || undefined;
          result.originalContext = data.context || undefined;
          if (data.images && data.images.length > 0) {
            const urls = await Promise.all(data.images.map((f) => resizeImageForStorage(f)));
            result.originalImages = urls.filter(Boolean) as string[];
          }
          updateItem(id, {
            status: 'done',
            read: false, // non lu → pastille bleue jusqu'à consultation
            result,
            score: result.score,
            verdict: result.verdict,
            serverId: resp.analysisId,
            progress: 100,
            progressLabel: undefined,
          });
          jobsRef.current.delete(id); // succès : on libère le payload (Files)
          // Preview admin « Bundle » : décrémente le crédit simulé du type.
          if (previewBundleRef.current) consumePreviewCredit(job.analysisType);
          pushNotification('analysis_done', id, job.inputSummary || job.label);
        } else {
          // Échec : on garde le payload en mémoire pour permettre un « réessayer ».
          updateItem(id, { status: 'error', errorMessage: resp.error ?? 'Une erreur est survenue.' });
          pushNotification('analysis_error', id, job.inputSummary || job.label);
        }
      } catch {
        updateItem(id, { status: 'error', errorMessage: 'Impossible de contacter le serveur.' });
        pushNotification('analysis_error', id, job.inputSummary || job.label);
      } finally {
        // Promote queued jobs now that a slot freed up.
        scheduleRef.current();
      }
    },
    [updateItem, pushNotification, consumePreviewCredit],
  );

  const schedule = useCallback(() => {
    const running = itemsRef.current.filter((i) => i.status === 'running').length;
    let slots = MAX_CONCURRENT_JOBS - running;
    if (slots <= 0) return;
    for (const it of itemsRef.current.filter((i) => i.status === 'queued')) {
      if (slots <= 0) break;
      slots--;
      void runJob(it.id);
    }
  }, [runJob]);

  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  const enqueue = useCallback(
    (input: AnalyzeJobInput): string => {
      const id = rid();
      const title =
        input.inputSummary.length > 60 ? input.inputSummary.slice(0, 57) + '…' : input.inputSummary;
      const item: SidebarItem = {
        id,
        title: title || input.label,
        analysisType: input.analysisType,
        analysisLabel: input.label,
        score: -1,
        timestamp: new Date().toISOString(),
        status: 'queued',
        progress: 0,
        read: true, // pertinent seulement une fois 'done' (passera à false)
      };
      jobsRef.current.set(id, input);
      setItemsSynced((prev) => [item, ...prev]);
      schedule();
      return id;
    },
    [setItemsSynced, schedule],
  );

  // ── Sidebar item mutations ────────────────────────────
  const renameItem = useCallback(
    (id: string, newTitle: string) => {
      const item = itemsRef.current.find((i) => i.id === id);
      updateItem(id, { title: newTitle });
      if (item?.serverId) updateServerAnalysis(item.serverId, { title: newTitle }).catch(() => {});
    },
    [updateItem],
  );

  const deleteItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((i) => i.id === id);
      setItemsSynced((prev) => prev.filter((i) => i.id !== id));
      setNotifications((prev) => prev.filter((n) => n.itemId !== id));
      persistCache();
      if (item?.serverId) deleteServerAnalysis(item.serverId).catch(() => {});
    },
    [setItemsSynced, persistCache],
  );

  const updateResult = useCallback(
    (id: string, result: ComplianceAnalysisResult) => {
      updateItem(id, { result });
    },
    [updateItem],
  );

  /** Relance une analyse en erreur (le payload est conservé en mémoire). */
  const retryItem = useCallback(
    (id: string) => {
      if (!jobsRef.current.has(id)) return;
      updateItem(id, { status: 'queued', errorMessage: undefined, progress: 0 });
      schedule();
    },
    [updateItem, schedule],
  );

  // ── Notifications ─────────────────────────────────────
  const markRead = useCallback((notifId: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === notifId ? { ...n, read: true } : n)));
  }, []);
  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);
  const markReadByItem = useCallback((itemId: string) => {
    setNotifications((prev) => prev.map((n) => (n.itemId === itemId ? { ...n, read: true } : n)));
    // Marque aussi l'item de la sidebar comme lu (retire la pastille bleue).
    setItemsSynced((prev) => prev.map((it) => (it.id === itemId && !it.read ? { ...it, read: true } : it)));
  }, [setItemsSynced]);
  const dismiss = useCallback((notifId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notifId));
  }, []);

  // ── Réattachement des jobs en cours (B1) ──────────────
  const stopPoll = useCallback((serverId: string) => {
    const t = pollersRef.current.get(serverId);
    if (t) { clearInterval(t); pollersRef.current.delete(serverId); }
  }, []);

  // Suit un job 'running' côté serveur jusqu'à done/error (poll toutes les 4 s).
  // Cas d'usage : l'onglet a été fermé/rafraîchi pendant l'analyse — le calcul a
  // continué côté serveur, on récupère le résultat à la reconnexion.
  const pollRunningJob = useCallback((serverId: string) => {
    if (pollersRef.current.has(serverId)) return; // déjà suivi
    let attempts = 0;
    const MAX_ATTEMPTS = 160; // ~11 min à 4 s
    const tick = async () => {
      attempts += 1;
      let rec: ServerAnalysisRecord | null = null;
      try { rec = await getServerAnalysis(serverId); } catch { /* réseau : on retentera */ }
      if (!rec) {
        if (attempts >= MAX_ATTEMPTS) stopPoll(serverId);
        return;
      }
      if (rec.status === 'running') {
        // Toujours refléter la vraie progression pendant que le job tourne —
        // sinon la jauge reste figée à 0 (valeur de création) jusqu'au 'done',
        // ce qui se lit comme « l'analyse a redémarré » alors qu'elle continue.
        updateItem(serverId, { progress: rec.progress ?? undefined, progressLabel: rec.progressLabel ?? undefined });
        if (attempts >= MAX_ATTEMPTS) stopPoll(serverId);
        return;
      }
      // rec.status est 'done' ou 'error' : le job est terminé, on arrête de poller.
      stopPoll(serverId);
      const title = itemsRef.current.find((i) => i.id === serverId)?.title ?? 'votre analyse';
      if (rec.status === 'done') {
        updateItem(serverId, {
          status: 'done', read: false,
          result: rec.result ?? undefined,
          score: rec.score ?? -1, verdict: rec.verdict ?? undefined,
          progress: 100, progressLabel: undefined,
        });
        pushNotification('analysis_done', serverId, title);
      } else {
        updateItem(serverId, { status: 'error', errorMessage: rec.errorMessage ?? 'Analyse échouée' });
        pushNotification('analysis_error', serverId, title);
      }
    };
    const timer = setInterval(() => { void tick(); }, 4000);
    pollersRef.current.set(serverId, timer);
    void tick(); // premier check immédiat
  }, [updateItem, stopPoll, pushNotification]);

  useEffect(() => { pollRef.current = pollRunningJob; }, [pollRunningJob]);

  // ── Hydratation : cache instantané puis fetch serveur ──
  useEffect(() => {
    // Reset complet au changement de compte (évite toute fuite inter-compte).
    jobsRef.current.clear();
    pollersRef.current.forEach((t) => clearInterval(t));
    pollersRef.current.clear();
    setNotifications([]);
    setActiveId(null);
    const cached = loadCache(userId);
    itemsRef.current = cached;
    setItems(cached);

    if (!hasHistoryAccess || !userId) return;
    let cancelled = false;
    listServerAnalyses({ period: 'all' })
      .then((records) => {
        if (cancelled) return;
        setItemsSynced((prev) => {
          const serverIds = new Set(records.map((r) => r.id));
          const prevById = new Map(prev.map((i) => [i.serverId ?? i.id, i]));
          const serverItems = records.map((r) => {
            const item = recordToItem(r);
            const existing = prevById.get(r.id);
            // Préserve une réécriture conforme générée localement que le serveur
            // n'a pas encore (le fix n'est pas re-poussé dans la colonne result).
            if (existing?.result?.suggestedFix && item.result && !item.result.suggestedFix) {
              item.result = { ...item.result, suggestedFix: existing.result.suggestedFix };
            }
            return item;
          });
          // On conserve les jobs vivants + les items locaux non persistés serveur.
          // Dédup par serverId (tous statuts) : le serveur fait foi, et un job
          // 'running' réattaché ne doit pas être dupliqué par un éventuel item local.
          const kept = prev.filter((it) => !serverIds.has(it.serverId ?? it.id));
          const all = [...kept, ...serverItems];
          all.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          return all;
        });
        persistCache();
        // Réattachement : on poll chaque job encore 'running' jusqu'à done/error.
        for (const r of records) {
          if (r.status === 'running') pollRef.current(r.id);
        }
      })
      .catch(() => {
        /* hors-ligne / erreur réseau : on garde le cache local */
      });
    return () => {
      cancelled = true;
      pollersRef.current.forEach((t) => clearInterval(t));
      pollersRef.current.clear();
    };
  }, [userId, hasHistoryAccess, setItemsSynced, persistCache]);

  const runningCount = items.filter((i) => i.status === 'running').length;
  const queuedCount = items.filter((i) => i.status === 'queued').length;
  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    items,
    activeId,
    setActiveId,
    enqueue,
    renameItem,
    deleteItem,
    updateResult,
    retryItem,
    notifications,
    toasts,
    dismissToast,
    unreadCount,
    runningCount,
    queuedCount,
    markRead,
    markAllRead,
    markReadByItem,
    dismiss,
  };
}
